import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { useNotification } from "config/Notification";

import dayjs from "dayjs";

import { useFormik } from "formik";

import * as Yup from "yup";

import { format } from "date-fns";

import {
  fetchCompanyUsers as fetchCompanyUsersService,
  fetchStandardsByUser as fetchStandardsByUserService,
  fetchAssessmentsByUser as fetchAssessmentsByUserService,
  saveAssignAssessment as saveAssignAssessmentService,
} from "services/modules/cap/standardservice";

import {
  putAssessmentStatus as putAssessmentStatusService,
  extendAssessmentDueDate as extendAssessmentDueDateService,
} from "services/modules/cap/assessmentservice";

import LoadingOverlay from "components/LoadingOverlay.jsx";

import "../../styles/cap.css";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";

const AssignAssessmentPage = () => {
  // State variables
  const [assessmentId, setAssessmentId] = useState(null);
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [cancel, setCancel] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [standardsList, setStandardsList] = useState([]);
  const [usersList, setUsersList] = useState([]);

  // US-CAP-301: Extend Due Date dialog state
  const [extendDialog, setExtendDialog] = useState(false);
  const [extendAssessmentId, setExtendAssessmentId] = useState(null);
  const [extendCurrentDueDate, setExtendCurrentDueDate] = useState(null);
  const [extendNewDueDate, setExtendNewDueDate] = useState(null);
  const [extendReason, setExtendReason] = useState("");
  const [extendErrors, setExtendErrors] = useState({});
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  // Redux selectors
  const selectedCompanyContainer = useSelector(
    (state) => state.auth.companyData,
  );

  // Notification
  const { openNotification } = useNotification();

  // Method defines the schema for form input values
  const validationSchema = Yup.object().shape({
    user: Yup.string().required("User is  not available for this company"),
    standard: Yup.string().required("Please select Standard"),
    dueDate: Yup.string().required("Please select Due Date"),
  });

  // Handles the form state, submission using useFormik hook
  const formik = useFormik({
    initialValues: {
      user: "",
      standard: "",
      dueDate: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      let body = {
        company: selectedCompanyContainer.companyid,
        standard: formik.values.standard,
        user: formik.values.user,
        due_date: format(new Date(formik.values.dueDate), "yyyy-MM-dd"),
      };
      let response = await saveAssignAssessmentService({ body });
      if (response.status === 201) {
        resetForm();
        getCompanyUsers();
      }
      setLoading(false);
    },
  });

  useEffect(() => {
    if (selectedCompanyContainer.companyid !== null) {
      formik.resetForm();
      getCompanyUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);

  // Method handles to get the users of the company
  const getCompanyUsers = async () => {
    try {
      setUsersList([]);
      let params = { company: selectedCompanyContainer.companyid };
      const response = await fetchCompanyUsersService({ params });
      if (response !== "" && response?.data?.length > 0) {
        let filtered_users_options = [];
        response.data.forEach((e) => {
          if (e.role === "User") {
            filtered_users_options.push(e);
          }
        });
        setUsersList((value) => (value = filtered_users_options));
        formik.setFieldValue("user", filtered_users_options[0]?.id);
      } else {
        formik.setFieldValue("user", "");
      }
    } catch (error) {
      openNotification("Error while fetching users data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formik.values.user !== "" && formik.values.user !== undefined) {
      getStandardsByUser();
      getAssessmentsByUser();
    }
    if (formik.values.user === "") {
      setAssessmentsList([]);
      setStandardsList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.user, cancel]);

  // Method to set the changes of assessment when it's status is changed
  const handleDialog = (assessment_id) => {
    setAssessmentId(assessment_id);
    setDialog(true);
  };

  // Method handles to change the status of assessment
  const handleStatusChange = async (assessmentId) => {
    try {
      let body = {
        assessment_id: assessmentId,
        status: "Cancelled",
      };
      let response = await putAssessmentStatusService({ body });
      if (response.data !== "") {
        openNotification("Assessment cancelled successfully", "success");
        setCancel(true);
      }
    } catch (error) {
      openNotification("Error while cancelling assessment", "error");
    } finally {
      setDialog(false);
    }
  };

  // US-CAP-301: Open the Extend Due Date dialog for a given assessment
  const handleOpenExtendDueDate = (assessment) => {
    setExtendAssessmentId(assessment.assessment_id);
    setExtendCurrentDueDate(assessment.due_date);
    setExtendNewDueDate(null);
    setExtendReason("");
    setExtendErrors({});
    setExtendDialog(true);
  };

  const handleExtendDateChange = (date) => {
    setExtendNewDueDate(date);
    setExtendErrors((prev) => ({ ...prev, newDueDate: undefined }));
  };

  // US-CAP-301: Validate then submit the due-date extension. A reason of at
  // least 10 characters is mandatory — mirrors the backend validation so the
  // user gets instant feedback instead of a round trip failure.
  const handleSubmitExtendDueDate = async () => {
    const errors = {};
    if (!extendNewDueDate) {
      errors.newDueDate = "Please select a new due date";
    } else if (dayjs(extendNewDueDate).isBefore(dayjs(), "day")) {
      errors.newDueDate = "New due date cannot be in the past";
    }
    if (!extendReason || extendReason.trim().length < 10) {
      errors.reason =
        "Please provide a reason (at least 10 characters) for extending the due date";
    }
    if (Object.keys(errors).length > 0) {
      setExtendErrors(errors);
      return;
    }

    setExtendSubmitting(true);
    try {
      const body = {
        assessment_id: extendAssessmentId,
        new_due_date: format(new Date(extendNewDueDate), "yyyy-MM-dd"),
        reason: extendReason.trim(),
      };
      const response = await extendAssessmentDueDateService(body);
      if (response?.status === 200 || response?.status === 201) {
        openNotification(
          response?.data?.message || "Due date extended successfully",
          "success",
        );
        setExtendDialog(false);
        getAssessmentsByUser();
      } else {
        openNotification(
          response?.data?.message || "Failed to extend due date",
          "error",
        );
      }
    } catch (error) {
      openNotification(
        error?.response?.data?.message || "Error while extending due date",
        "error",
      );
    } finally {
      setExtendSubmitting(false);
    }
  };

  // Method handles to fetch the selected standards by user
  const getStandardsByUser = async () => {
    setLoading(true);
    try {
      setStandardsList([]);
      let params = {
        companyid: selectedCompanyContainer.companyid,
        userid: formik.values.user,
      };
      const response = await fetchStandardsByUserService({ params });
      if (response !== "") {
        setStandardsList((value) => (value = response));
      }
    } catch (error) {
      openNotification("Error while fetching the selected standards", "error");
    } finally {
      setLoading(false);
    }
  };

  // Method to fetch the assessments of user
  const getAssessmentsByUser = async () => {
    try {
      setAssessmentsList([]);
      let params = {
        companyid: selectedCompanyContainer.companyid,
        userid: formik.values.user,
      };
      const response = await fetchAssessmentsByUserService({ params });
      if (response !== "") {
        setAssessmentsList(response);
      }
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  // This method handles to change the pages
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // This method handles to change the rows per page based on user selection
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Function to handle date picker change
  const handleDateChange = (date) => {
    formik.setFieldValue("dueDate", date);
  };

  return (
    <div>
      <div className="row">
        <b className="px-4 pt-3">Assign assessments</b>
      </div>
      {loading && <LoadingOverlay />}
      <hr />

      <div className="m-1">
        <form onSubmit={formik.handleSubmit}>
          <div className="col m-4">
            <div className="row mt-2">
              <div className="col-xl-4 col-lg-4 col-md-4 col-sm-6 col-xs-12">
                <FormControl
                  fullWidth
                  error={formik.touched.user && Boolean(formik.errors.user)}
                >
                  <InputLabel htmlFor="user">User</InputLabel>
                  <Select
                    label="User"
                    id="user"
                    name="user"
                    value={formik.values.user}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {usersList.length > 0 &&
                      usersList.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {formik.touched.user && formik.errors.user && (
                    <FormHelperText className="clr-r">
                      {formik.errors.user}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
              <div className="col-xl-4 col-lg-4 col-md-4 col-sm-6 col-xs-12">
                <FormControl
                  fullWidth
                  error={
                    formik.touched.standard &&
                    formik.values.user !== "" &&
                    Boolean(formik.errors.standard)
                  }
                >
                  <InputLabel htmlFor="standard">Standard</InputLabel>
                  <Select
                    label="Standard"
                    id="standard"
                    name="standard"
                    value={formik.values.standard}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.user}
                  >
                    {standardsList.length > 0 &&
                      standardsList.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {formik.touched.standard &&
                    formik.values.user !== "" &&
                    formik.errors.standard && (
                      <FormHelperText className="clr-r">
                        {formik.errors.standard}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>
              <div className="col-xl-4 col-lg-4 col-md-4 col-sm-6 col-xs-12">
                <FormControl
                  fullWidth
                  error={
                    formik.touched.standard && Boolean(formik.errors.standard)
                  }
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Due Date"
                      minDate={dayjs(new Date())}
                      id="dueDate"
                      name="dueDate"
                      value={formik.values.dueDate}
                      onChange={handleDateChange}
                      onBlur={formik.handleBlur}
                      disabled={!formik.values.user}
                      renderInput={(params) => <input {...params} />}
                    />
                  </LocalizationProvider>
                  {formik.touched.dueDate &&
                    formik.errors.dueDate &&
                    formik.values.user !== "" && (
                      <FormHelperText className="clr-r">
                        {formik.errors.dueDate}
                      </FormHelperText>
                    )}
                </FormControl>
              </div>
            </div>
            <div className="row justify-center">
              <Button type="submit" className="assign-btn">
                Assign
              </Button>
            </div>
          </div>
        </form>

        {assessmentsList && assessmentsList.length > 0 && (
          <div>
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer sx={{ maxHeight: 440, overflowX: "auto" }}>
                <Table stickyHeader sx={{ minWidth: { xs: 720, md: "auto" } }}>
                  <TableHead>
                    <TableRow key="1">
                      <TableCell>Assessment id</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Standard</TableCell>
                      <TableCell>Assigned date</TableCell>
                      <TableCell>Due date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assessmentsList.length > 0 &&
                      assessmentsList.map((item) => {
                        const isOnHold = item.assessments.status === "On Hold";
                        const isClosed = ["Completed", "Cancelled"].includes(
                          item.assessments.status,
                        );
                        return (
                          <React.Fragment key={item.id}>
                            <TableRow>
                              <TableCell className="text-blue text-bold">
                                {item.assessments.assessment_id}
                              </TableCell>
                              <TableCell>{item.assessments.username}</TableCell>
                              <TableCell>{item.standard}</TableCell>
                              <TableCell>
                                {new Date(
                                  item.assessments.created_date,
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  item.assessments.due_date,
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {item.assessments.status === "Open" ? (
                                  <Select
                                    value="Open"
                                    size="small"
                                    className="dropdown-font"
                                    sx={{
                                      minWidth: { xs: 90, sm: 110 },
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.875rem",
                                      },
                                    }}
                                    onChange={(e) =>
                                      handleDialog(
                                        item.assessments.assessment_id,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <MenuItem value="Open" disabled>
                                      Open
                                    </MenuItem>
                                    <MenuItem value="Cancelled">
                                      Cancel
                                    </MenuItem>
                                  </Select>
                                ) : (
                                  <Chip
                                    variant={isOnHold ? "filled" : "outlined"}
                                    color={isOnHold ? "warning" : "default"}
                                    label={item.assessments.status}
                                    size="small"
                                    sx={{
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.8125rem",
                                      },
                                      maxWidth: "100%",
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {!isClosed && (
                                  <Tooltip
                                    title={
                                      isOnHold
                                        ? "Due date has passed — extend it to let the user continue"
                                        : "Extend due date"
                                    }
                                  >
                                    <Button
                                      size="small"
                                      variant={isOnHold ? "contained" : "text"}
                                      color={isOnHold ? "warning" : "primary"}
                                      onClick={() =>
                                        handleOpenExtendDueDate(
                                          item.assessments,
                                        )
                                      }
                                      sx={{
                                        whiteSpace: "nowrap",
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.8125rem",
                                        },
                                        px: { xs: 1, sm: 2 },
                                        minWidth: "auto",
                                      }}
                                    >
                                      Extend Due Date
                                    </Button>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={assessmentsList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>

            <Dialog open={dialog} onClose={() => setDialog(false)}>
              <DialogTitle align="center">
                <ErrorOutlineIcon fontSize="large" color="error" />
              </DialogTitle>
              <DialogContent align="center">
                Are you sure you want to cancel the assessment?
              </DialogContent>
              <DialogActions className="m-3">
                <Button
                  onClick={() => setDialog(false)}
                  variant="contained"
                  className="btn-card danger"
                >
                  No
                </Button>
                <Button
                  onClick={() => handleStatusChange(assessmentId)}
                  variant="contained"
                  className="btn-card"
                >
                  Yes
                </Button>
              </DialogActions>
            </Dialog>

            {/* US-CAP-301: Extend Due Date dialog */}
            <Dialog
              open={extendDialog}
              onClose={() => setExtendDialog(false)}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>Extend Due Date</DialogTitle>
              <DialogContent>
                <div
                  className="mb-3"
                  style={{ fontSize: "13px", color: "#666" }}
                >
                  Assessment: <b>{extendAssessmentId}</b>
                  <br />
                  Current due date:{" "}
                  <b>
                    {extendCurrentDueDate
                      ? new Date(extendCurrentDueDate).toLocaleDateString()
                      : "—"}
                  </b>
                </div>
                <FormControl
                  fullWidth
                  error={Boolean(extendErrors.newDueDate)}
                  sx={{ mb: 2 }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="New Due Date"
                      minDate={dayjs(new Date())}
                      value={extendNewDueDate}
                      onChange={handleExtendDateChange}
                      renderInput={(params) => <input {...params} />}
                    />
                  </LocalizationProvider>
                  {extendErrors.newDueDate && (
                    <FormHelperText className="clr-r">
                      {extendErrors.newDueDate}
                    </FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label="Reason for extension"
                  placeholder="e.g. User was on leave and requested more time to complete the assessment"
                  multiline
                  minRows={3}
                  fullWidth
                  value={extendReason}
                  onChange={(e) => {
                    setExtendReason(e.target.value);
                    setExtendErrors((prev) => ({ ...prev, reason: undefined }));
                  }}
                  error={Boolean(extendErrors.reason)}
                  helperText={
                    extendErrors.reason ||
                    `${extendReason.trim().length}/10 characters minimum`
                  }
                />
              </DialogContent>
              <DialogActions
                className="m-3"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                }}
              >
                <Button
                  onClick={() => setExtendDialog(false)}
                  variant="contained"
                  className="danger"
                  disabled={extendSubmitting}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                    minWidth: 100,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitExtendDueDate}
                  variant="contained"
                  disabled={extendSubmitting}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "0 1 auto" },
                    minWidth: 100,
                  }}
                >
                  {extendSubmitting ? "Extending..." : "Extend"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignAssessmentPage;
